import React, { useState, useEffect } from "react";
import Logo from "../assets/images/commaReportImg.png";
import ReportSummaryPage from "./ReportSummaryPage";
import SentenceBreakdownPage from "./SentenceBreakdownPage";
import axios from "axios";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, Navigate } from "react-router-dom";

export default function ReportPage() {
  const [view, setView] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const location = useLocation();
  const { state } = location;

  // If no state was passed, redirect to create report page
  if (!state?.feedId || !state?.userId) {
    return <Navigate to="/report/create" replace />;
  }

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://genz-staging.feeltiptop.com/api/reports/by-feed`,
          {
            params: {
              feedId: state.feedId,
              userId: state.userId,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          },
        );

        if (response.data.success) {
          // Merge the backend data with the state data
          const mergedData = {
            ...response.data.data,
            patient: {
              ...response.data.data.patient,
              name: state.userName || response.data.data.patient.name,
              email: state.userEmail || response.data.data.patient.email,
            },
            feed: {
              name: state.feedName,
              date: state.feedDate,
            },
          };
          setReportData(mergedData);
        } else {
          setError(response.data.message || "Failed to fetch report data");
        }
      } catch (err) {
        setError("Error fetching report data: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [state]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#4979D1]">
        <div className="text-center text-white">
          <div className="mb-4 text-xl">Loading report...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#4979D1]">
        <div className="text-center text-white">
          <div className="mb-4 text-xl text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#4979D1]">
        <div className="text-center text-white">
          <div className="mb-4 text-xl">No report data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-inter flex min-h-screen bg-[#4979D1]">
      {/* Sidebar */}
      <aside className="flex w-64 flex-col items-start bg-[#4979D1] p-8 text-white">
        <div className="mb-8 flex items-start space-x-2">
          <div
            className="group relative flex items-center"
            style={{ minHeight: 80 }}
          >
            {/* Logo and Title */}
            <div
              className="duration-400 flex items-center transition-all ease-in-out"
              style={{}}
            >
              {/* Animated Back Button */}
              <button
                onClick={() => (window.location.href = "/CommaDashboard")}
                className="duration-400 pointer-events-none z-10 -translate-x-14 transform transition-all ease-in-out group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100"
              >
                <ArrowBack className="h-6 w-6 text-white" />
              </button>
              <button
                onClick={() => (window.location.href = "/CommaDashboard")}
              >
                <img
                  src={Logo}
                  alt="COMMA logo"
                  className="w-85 duration-400 h-20 transition-all ease-in-out group-hover:ml-5"
                  style={{
                    transition: "margin-left 0.4s cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="mb-12 flex flex-col items-start">
          <div className="mb-4 h-24 w-24 overflow-hidden rounded-full border-2 border-white">
            <img
              src={`https://api.dicebear.com/7.x/initials/svg?seed=${reportData.patient.name}`}
              alt={reportData.patient.name}
              className="h-full w-full object-cover"
            />
          </div>
          <h2 className="mb-2 text-left text-2xl font-light">
            {reportData.patient.name}
          </h2>
          <div className="space-y-1 text-left text-sm text-white/90">
            <p>Patient ID: #{String(reportData.patient.id).padStart(5, "0")}</p>
            <p>Age: {reportData.patient.age}</p>
            <p>Gender: {reportData.patient.gender}</p>
            <p>Date of Evaluation: {reportData.patient.date}</p>
            {reportData.feed && (
              <>
                <p>
                  Session Date:{" "}
                  {new Date(reportData.feed.date).toLocaleDateString()}
                </p>
                <p>Type: {reportData.feed.name}</p>
              </>
            )}
          </div>
        </div>

        <nav className="w-full space-y-4">
          <button
            onClick={() => setView("summary")}
            className={`w-full rounded-lg py-2 pl-4 text-left transition ${
              view === "summary"
                ? "bg-white/10 font-medium"
                : "hover:bg-white/5"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setView("breakdown")}
            className={`w-full rounded-lg py-2 pl-4 text-left transition ${
              view === "breakdown"
                ? "bg-white/10 font-medium"
                : "hover:bg-white/5"
            }`}
          >
            Sentence Breakdown
          </button>
        </nav>
      </aside>

      {/* Content area */}
      <div className="bg-blue flex-1 overflow-y-auto">
        {view === "summary" ? (
          <ReportSummaryPage
            patient={reportData.patient}
            report={reportData.report}
            taskData={reportData.taskData.map((task) => ({
              ...task,
              original: {
                ...task.original,
                audioUrl:
                  task.original.audioUrl &&
                  !task.original.audioUrl.startsWith("media/")
                    ? `media/assets/${task.original.audioUrl}`
                    : task.original.audioUrl,
              },
            }))}
          />
        ) : (
          <SentenceBreakdownPage
            patient={reportData.patient}
            taskData={reportData.taskData.map((task) => ({
              ...task,
              original: {
                ...task.original,
                audioUrl:
                  task.original.audioUrl &&
                  !task.original.audioUrl.startsWith("media/")
                    ? `media/assets/${task.original.audioUrl}`
                    : task.original.audioUrl,
              },
            }))}
          />
        )}
      </div>
    </div>
  );
}
